import React from "react";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import { useModalConfirmation, useQueryData, useToast } from "hooks";
import { GET_MISSION_ROLE_TEMPLATES } from "graphql/queries";
import { MISSION_ROLE_TEMPLATE_STATE, isBlueprintAgentTask, isBlueprintPublished } from "constants/index";
import { REMOVE_MISSION_ROLE_TEMPLATE, UPDATE_MISSION_ROLE_TEMPLATE } from "graphql/mutations";

/**
 * useMissionRoleTemplates
 *
 * @param {Boolean}  skip  Leave the list unread, for a caller that only removes blueprints
 */
export default function useMissionRoleTemplates({ skip = false } = {}) {
  const { toast } = useToast();
  const { show } = useModalConfirmation();
  const { data, loading, error } = useQueryData({
    queryName: GET_MISSION_ROLE_TEMPLATES,
    keyName: "missionRoleTemplates",
    disablePagination: true,
    skip,
  });
  const [removeTemplate, { loading: removingTemplate }] = useMutation(REMOVE_MISSION_ROLE_TEMPLATE);
  const [updateTemplate, { loading: togglingPublished }] = useMutation(UPDATE_MISSION_ROLE_TEMPLATE);

  const handleRemoveTemplate = async ({ template, onCompleted }) => {
    const isAgentRequest = isBlueprintAgentTask(template);
    const messageFragment = isAgentRequest ? "Blueprints.Discard" : "Blueprints.Remove";
    const confirmed = await show(
      <FormattedMessage id={`${messageFragment}.Confirmation.Title`} />,
      <FormattedMessage id={`${messageFragment}.Confirmation.Description`} />,
      {
        showCancel: true,
        showSave: false,
        showSaveAsDestructable: true,
        primaryButtonMessageId: `${messageFragment}.Confirmation.Confirm`,
      }
    );

    if (!confirmed) return;

    return removeTemplate({
      variables: { input: { id: template.uuid } },
      refetchQueries: [GET_MISSION_ROLE_TEMPLATES],
      onCompleted: () => {
        toast.success(<FormattedMessage id={`${messageFragment}.Success`} />);
        onCompleted?.();
      },
      onError: (error) => toast.error(error.message || <FormattedMessage id={`${messageFragment}.Error`} />),
    });
  };

  const handleTogglePublished = (template) => {
    const messageFragment = isBlueprintPublished(template) ? "Blueprints.Unpublish" : "Blueprints.Publish";
    const state = isBlueprintPublished(template)
      ? MISSION_ROLE_TEMPLATE_STATE.unpublished
      : MISSION_ROLE_TEMPLATE_STATE.published;

    return updateTemplate({
      variables: { input: { id: template.uuid, params: { state } } },
      onCompleted: () => toast.success(<FormattedMessage id={`${messageFragment}.Success`} />),
      onError: () => toast.error(<FormattedMessage id={`${messageFragment}.Error`} />),
    });
  };

  return {
    templates: data?.missionRoleTemplates?.nodes || [],
    loading,
    error,
    handleRemoveTemplate,
    removingTemplate,
    handleTogglePublished,
    togglingPublished,
  };
}
